import type {
  SelvesInput,
  SelvesState,
  InterestId,
  StrengthId,
  AmbitionId,
  LifePathId,
} from "./types";
import {
  INTEREST_ORDER,
  STRENGTH_ORDER,
  AMBITION_ORDER,
  LIFE_PATH_ORDER,
} from "./types";
import { NICKNAME_MAX } from "./options";
import { encodeConfig, decodeConfig } from "@/lib/universe";

/**
 * Nickname validation according to spec:
 * - trim and collapse multiple internal whitespaces to a single space
 * - empty string is completely fine (falls back to Traveler)
 * - max 24 chars
 * - allowed regex: letters, numbers, spaces, apostrophes, periods, underscores, hyphens
 * - rejection with clear error message for:
 *     - length > 24
 *     - contains '@'
 *     - looks like a URL (http, https, www., or .com/.org/.net/.io/.dev)
 *     - 7+ digits ("use a nickname, not contact details")
 *     - disallowed characters
 */
export function sanitizeNickname(raw: string): { value: string; error: string | null } {
  if (typeof raw !== "string") {
    return { value: "", error: null };
  }

  // Collapse whitespaces
  const trimmed = raw.trim().replace(/\s+/g, " ");
  if (trimmed === "") {
    return { value: "", error: null };
  }

  if (trimmed.length > NICKNAME_MAX) {
    return {
      value: trimmed.slice(0, NICKNAME_MAX),
      error: `Nickname must be ${NICKNAME_MAX} characters or fewer.`,
    };
  }

  if (trimmed.includes("@")) {
    return {
      value: trimmed,
      error: "Please enter a friendly nickname rather than an email address.",
    };
  }

  if (/https?:\/\/|www\.|\.(com|org|net|io|dev|app|edu|gov|co)(\/|\b)/i.test(trimmed)) {
    return {
      value: trimmed,
      error: "Please enter a nickname rather than a web link or URL.",
    };
  }

  // Count total digits
  const digitMatches = trimmed.match(/\d/g);
  if (digitMatches && digitMatches.length >= 7) {
    return {
      value: trimmed,
      error: "Use a nickname, not contact details or phone numbers.",
    };
  }

  // Spec: allowed /^[\p{L}\p{N}][\p{L}\p{N} '’._-]*$/u
  const allowedPattern = /^[\p{L}\p{N}][\p{L}\p{N} '’._-]*$/u;
  if (!allowedPattern.test(trimmed)) {
    return {
      value: trimmed,
      error: "Nickname may only use letters, numbers, spaces, dots, hyphens, and apostrophes.",
    };
  }

  return { value: trimmed, error: null };
}

/**
 * Standard browser-safe UTF-8 Base64URL encoder without Node Buffer.
 */
function utf8ToBase64Url(str: string): string {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(str);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Standard browser-safe UTF-8 Base64URL decoder without Node Buffer.
 * Returns null if invalid base64 or invalid UTF-8.
 */
function base64UrlToUtf8(b64url: string): string | null {
  try {
    // Only URL-safe base64 characters allowed
    if (!/^[A-Za-z0-9_-]*$/.test(b64url)) return null;
    let b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4 !== 0) {
      b64 += "=";
    }
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const decoder = new TextDecoder("utf-8", { fatal: true });
    return decoder.decode(bytes);
  } catch {
    return null;
  }
}

/**
 * Encode state to a compact, URL-safe string.
 * Format:
 *   1.<b64nick>.<interests>.<strengths>.<ambition>.<lifePath>.<v0>-<v1>-<v2>-<v3>.<universe>
 *
 * Rules:
 * - Version prefix "1."
 * - Empty nickname encoded as "~"
 * - interests: underscore-separated or "~" if empty (must be canonical order)
 * - strengths: underscore-separated or "~" if empty (must be canonical order)
 * - ambition: ambition id or "~"
 * - lifePath: lifePath id or "~"
 * - variants: 4 numbers separated by '-' (each 0..999999)
 * - universe: encodeConfig(config) or "0"
 *
 * All characters in the result are strictly [A-Za-z0-9._~-]
 */
export function encodeState(state: SelvesState): string {
  const sanitizedNick = sanitizeNickname(state.input.nickname).value;
  const nickToken = sanitizedNick ? utf8ToBase64Url(sanitizedNick) : "~";

  const interestToken = state.input.interests.length > 0
    ? state.input.interests.join("_")
    : "~";

  const strengthToken = state.input.strengths.length > 0
    ? state.input.strengths.join("_")
    : "~";

  const ambitionToken = state.input.ambition || "~";
  const lifePathToken = state.input.lifePath || "~";

  const variantsToken = state.variants.join("-");
  const universeToken = state.universe ? encodeConfig(state.universe) : "0";

  return `1.${nickToken}.${interestToken}.${strengthToken}.${ambitionToken}.${lifePathToken}.${variantsToken}.${universeToken}`;
}

/**
 * Strict state decoding.
 * Returns null for:
 * - non-string or malformed format
 * - unknown version
 * - invalid nickname base64, nickname > 24 chars, or disallowed chars
 * - non-canonical / duplicate / unknown interest or strength ids
 * - unknown ambition or life path ids
 * - out of range variant integers (<0 or >999999 or not 4 entries)
 * - invalid universe representation (must match decodeConfig or be "0")
 * - canonical re-encoding mismatch (ensures no non-canonical data survives)
 * Never throws.
 */
export function decodeState(s: string): SelvesState | null {
  if (!s || typeof s !== "string") return null;

  // Strict allowed characters check
  if (!/^[A-Za-z0-9._~-]+$/.test(s)) return null;

  const parts = s.split(".");
  // We expect: version(1), nick, interests, strengths, ambition, lifePath, variants, universe
  // Note: universe from encodeConfig might contain hyphens, but no dots!
  if (parts.length !== 8) return null;

  const [version, nickPart, interestPart, strengthPart, ambitionPart, lifePathPart, variantsPart, universePart] = parts;

  if (version !== "1") return null;

  // Decode nickname
  let nickname = "";
  if (nickPart !== "~") {
    const decodedNick = base64UrlToUtf8(nickPart);
    if (decodedNick === null) return null;
    const { value, error } = sanitizeNickname(decodedNick);
    if (error !== null || value !== decodedNick) {
      return null;
    }
    nickname = value;
  }

  // Interests
  const interests: InterestId[] = [];
  if (interestPart !== "~") {
    const rawIds = interestPart.split("_");
    if (rawIds.length > 3 || rawIds.length === 0) return null;

    // Check validity and uniqueness
    const seen = new Set<string>();
    let lastIndex = -1;
    for (const rawId of rawIds) {
      const idx = INTEREST_ORDER.indexOf(rawId as InterestId);
      if (idx === -1 || seen.has(rawId)) return null;
      // Must be in strict canonical order
      if (idx <= lastIndex) return null;
      seen.add(rawId);
      lastIndex = idx;
      interests.push(rawId as InterestId);
    }
  }

  // Strengths
  const strengths: StrengthId[] = [];
  if (strengthPart !== "~") {
    const rawIds = strengthPart.split("_");
    if (rawIds.length > 3 || rawIds.length === 0) return null;

    const seen = new Set<string>();
    let lastIndex = -1;
    for (const rawId of rawIds) {
      const idx = STRENGTH_ORDER.indexOf(rawId as StrengthId);
      if (idx === -1 || seen.has(rawId)) return null;
      if (idx <= lastIndex) return null;
      seen.add(rawId);
      lastIndex = idx;
      strengths.push(rawId as StrengthId);
    }
  }

  // Ambition
  let ambition: AmbitionId | null = null;
  if (ambitionPart !== "~") {
    if (!AMBITION_ORDER.includes(ambitionPart as AmbitionId)) return null;
    ambition = ambitionPart as AmbitionId;
  }

  // Life Path
  let lifePath: LifePathId | null = null;
  if (lifePathPart !== "~") {
    if (!LIFE_PATH_ORDER.includes(lifePathPart as LifePathId)) return null;
    lifePath = lifePathPart as LifePathId;
  }

  // Variants
  const varParts = variantsPart.split("-");
  if (varParts.length !== 4) return null;
  const variantsArr: number[] = [];
  for (const vp of varParts) {
    if (!/^\d+$/.test(vp)) return null;
    const v = parseInt(vp, 10);
    if (v < 0 || v > 999999) return null;
    variantsArr.push(v);
  }
  const variants: [number, number, number, number] = [
    variantsArr[0],
    variantsArr[1],
    variantsArr[2],
    variantsArr[3],
  ];

  // Universe
  let universe = null;
  if (universePart !== "0") {
    universe = decodeConfig(universePart);
    if (!universe) return null;
  }

  const result: SelvesState = {
    input: {
      nickname,
      interests,
      strengths,
      ambition,
      lifePath,
    },
    variants,
    universe,
  };

  // Canonical round-trip test: ensures exact equivalence
  if (encodeState(result) !== s) {
    return null;
  }

  return result;
}
